import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Check, Crown, Star, Zap, BookOpen, Sparkles, Globe, Focus } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useUserProfile } from '../hooks/useUserProfile';
import { useRevenueCat } from '../hooks/useRevenueCat';
import { useTranslation } from '../contexts/TranslationContext';
import ParentalPin from './ParentalPin';

interface SubscriptionScreenProps {
  onBack: () => void;
  onSubscriptionComplete: (plan: 'monthly' | 'yearly') => void;
}

interface FeatureSlide {
  icon: React.ReactNode;
  title: string;
  description: string;
  image?: string;
}

// Feature slides will be built with translations in the component

const SubscriptionScreen: React.FC<SubscriptionScreenProps> = ({
  onBack,
  onSubscriptionComplete,
}) => {
  const { profile, user, authProvider } = useUserProfile();
  const {
    isInitialized,
    isLoading: revenueCatLoading,
    error: revenueCatError,
    monthlyPackage,
    annualPackage,
    purchasePackage,
    restorePurchases,
    isPremium,
  } = useRevenueCat(user?.id);
  const { t } = useTranslation();

  const isNative = Capacitor.isNativePlatform();
  const isWeb = Capacitor.getPlatform() === 'web';

  // Build feature slides with translations
  const featureSlides: FeatureSlide[] = [
    {
      icon: <BookOpen size={32} className="text-white" />,
      title: t("subscription.autonomous_reading_for_ages_58", "Autonomous reading for ages 5-8"),
      description: t("subscription.your_child_decides_multiple_choices_narrative_surp", "Your child decides: multiple choices, narrative surprises and mini-games to stay captivated."),
    },
    {
      icon: <Focus size={32} className="text-white" />,
      title: t("subscription.lire_pas_consommer", "Lire, pas consommer"),
      description: t("subscription.encouragez_la_lecture_active_pour_prepar", "Encouragez la lecture active pour préparer la transition vers les romans non illustrés. Moins d'écran passif, de dessins animés, de lecture audio automatique : ici, l'enfant apprend à aimer la lecture en s'impliquant."),
    },
    {
      icon: <Sparkles size={32} className="text-white" />,
      title: t("subscription.des_aventures_quotidiennes", "Des aventures quotidiennes"),
      description: t("subscription.grace_a_une_rotation_quotidienne_de_nos_aventures", "With a daily rotation of our adventures, LilHero encourages a simple routine: 20 minutes of reading per day with regular exclusive content."),
    },
    {
      icon: <Globe size={32} className="text-white" />,
      title: t("subscription.des_univers_varies_pour_nourrir_son_imaginaire", "Varied universes to nurture imagination"),
      description: t("subscription.lilhero_propose_une_grande_diversite_de", "LilHero propose une grande diversité de mondes pour stimuler la créativité de votre enfant, avec des personnages récurrents qu'il aura plaisir à retrouver d'une aventure à l'autre."),
    },
    {
      icon: <Sparkles size={32} className="text-white" />,
      title: t("subscription.universe_customization", "Universe customization"),
      description: t("subscription.votre_enfant_pourra_batir_son_propre_monde_et_stim", "Your child can build their own world and stimulate their creativity"),
    },
  ];
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [showPinVerification, setShowPinVerification] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Web - Stripe checkout state
  const [stripePrices, setStripePrices] = useState<{
    monthly: { formatted: string; amount: number } | null;
    yearly: { formatted: string; amount: number } | null;
  }>({ monthly: null, yearly: null });
  const [stripeLoading, setStripeLoading] = useState(false);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Auto-advance carousel (5 seconds, pauses 10s after user interaction)
  useEffect(() => {
    if (userInteracted) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featureSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [userInteracted]);

  // Handle user interaction with carousel
  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
    setUserInteracted(true);

    // Clear existing timeout
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    // Resume auto-advance after 10 seconds of no interaction
    interactionTimeoutRef.current = setTimeout(() => {
      setUserInteracted(false);
    }, 10000);
  };

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentSlide < featureSlides.length - 1) {
        // Swipe left - next slide
        handleSlideChange(currentSlide + 1);
      } else if (diff < 0 && currentSlide > 0) {
        // Swipe right - previous slide
        handleSlideChange(currentSlide - 1);
      }
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  // If already premium, redirect
  useEffect(() => {
    if (isPremium && isInitialized) {
      onSubscriptionComplete(selectedPlan);
    }
  }, [isPremium, isInitialized, onSubscriptionComplete, selectedPlan]);

  // Fetch Stripe prices on web
  useEffect(() => {
    if (!isWeb) return;

    const fetchStripePrices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-checkout`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
            },
            body: JSON.stringify({ action: 'get-prices' }),
          }
        );
        const data = await response.json();
        if (data.monthly && data.yearly) {
          setStripePrices(data);
        }
      } catch (err) {
        console.error('Failed to fetch Stripe prices:', err);
      }
    };

    fetchStripePrices();
  }, [isWeb]);

  // Get package info for display
  const getPackagePrice = (pkg: typeof monthlyPackage) => {
    if (!pkg) return null;
    return pkg.product?.priceString || '';
  };

  const monthlyPrice = getPackagePrice(monthlyPackage);
  const yearlyPrice = getPackagePrice(annualPackage);

  // Calculate monthly equivalent for yearly
  const yearlyMonthlyEquivalent = annualPackage?.product?.price
    ? `${(annualPackage.product.price / 12).toFixed(2)} €/mois`
    : null;

  const handleSubscribe = () => {
    setError(null);
    // Si PIN configuré, demander vérification avant paiement
    if (profile?.parentPinHash) {
      setShowPinVerification(true);
    } else {
      // Pas de PIN, aller directement au paiement
      handlePurchase();
    }
  };

  const handlePinSuccess = () => {
    setShowPinVerification(false);
    handlePurchase();
  };

  const handlePurchase = async () => {
    const packageToPurchase = selectedPlan === 'yearly' ? annualPackage : monthlyPackage;

    if (!packageToPurchase) {
      setError(t("subscription.produit_non_disponible", "Produit non disponible"));
      return;
    }

    setPurchaseLoading(true);
    setError(null);

    try {
      const result = await purchasePackage(packageToPurchase);

      if (result.success) {
        setPurchaseSuccess(true);
        // Après 2 secondes, finaliser
        setTimeout(() => {
          onSubscriptionComplete(selectedPlan);
        }, 2000);
      } else if (!result.cancelled) {
        setError(result.error || t("subscription.echec_de_lachat", "Échec de l'achat"));
      }
    } catch (err: any) {
      setError(err.message || t("subscription.echec_de_lachat", "Échec de l'achat"));
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleRestore = async () => {
    setRestoreLoading(true);
    setError(null);

    try {
      const result = await restorePurchases();

      if (result.success && result.isPremium) {
        setPurchaseSuccess(true);
        setTimeout(() => {
          onSubscriptionComplete(selectedPlan);
        }, 2000);
      } else if (result.success && !result.isPremium) {
        setError(t("subscription.aucun_achat_a_restaurer", "No purchases to restore"));
      } else {
        setError(result.error || t("subscription.echec_de_la_restauration", "Échec de la restauration"));
      }
    } catch (err: any) {
      setError(err.message || t("subscription.echec_de_la_restauration", "Échec de la restauration"));
    } finally {
      setRestoreLoading(false);
    }
  };

  // Handle Stripe checkout (web only)
  const handleStripeCheckout = async () => {
    setStripeLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          },
          body: JSON.stringify({
            action: 'create-checkout',
            priceType: selectedPlan,
            email: user?.email,
            userId: user?.id,
            successUrl: `${window.location.origin}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
          }),
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (err: any) {
      setError(err.message || t("subscription.echec_de_lachat", "Échec de l'achat"));
      setStripeLoading(false);
    }
  };

  // Écran de vérification PIN
  if (showPinVerification) {
    return (
      <ParentalPin
        mode="verify"
        currentPinHash={profile?.parentPinHash}
        userEmail={user?.email}
        authProvider={authProvider}
        title={t("subscription.parental_verification", "Parental verification")}
        subtitle={t("subscription.enter_your_pin_to_confirm_the_purchase", "Enter your PIN to confirm the purchase")}
        onSuccess={handlePinSuccess}
        onCancel={() => setShowPinVerification(false)}
      />
    );
  }

  // Écran de succès
  if (purchaseSuccess) {
    return (
      <div className={`h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Crown size={40} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {t("subscription.paiement_reussi", "Payment successful!")}
            </h1>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Check size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              {t("subscription.bienvenue_dans_lilhero_premium", "Bienvenue dans LilHero Premium !")}
            </h2>
            <p className="text-gray-600 mb-4">
              {t("subscription.votre_abonnement_est_maintenant_actif_profitez_de", "Votre abonnement est maintenant actif. Profitez de toutes les histoires !")}
            </p>
            <div className="animate-pulse text-purple-600 font-medium">
              {t("subscription.redirection_en_cours", "Redirection en cours...")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Web - show Stripe checkout (same UI as native but with Stripe prices)
  if (isWeb) {
    const webMonthlyPrice = stripePrices.monthly?.formatted || '7,99 €';
    const webYearlyPrice = stripePrices.yearly?.formatted || '49,99 €';
    const webYearlyMonthly = stripePrices.yearly
      ? `${(stripePrices.yearly.amount / 12).toFixed(2)} €/mois`
      : '4,17 €/mois';

    return (
      <div className="h-screen-adaptive bg-gradient-to-b from-purple-600 via-pink-500 to-orange-400">
        <div className="px-4 py-4">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-full hover:bg-white/20 transition-colors"
          >
            <ChevronLeft size={28} className="text-white" />
          </button>
        </div>

        <div className="px-4 pb-8">
          <div className="text-center mb-6">
            <img
              src="/logo-white.png"
              alt="LilHero"
              className="h-16 w-auto mx-auto mb-2 drop-shadow-lg"
            />
            <h1 className="text-xl font-bold text-white mb-1 font-title">
              {t("subscription.ici_votre_enfant_est_le_heros", "Ici, votre enfant est le héros")}
            </h1>
          </div>

          {/* Features Carousel - same as native */}
          <div className="mb-4">
            <div
              ref={carouselRef}
              className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {featureSlides.map((slide, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0 p-4 text-center"
                  >
                    {slide.image ? (
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-24 object-cover rounded-xl mb-3"
                      />
                    ) : (
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
                        {slide.icon}
                      </div>
                    )}
                    <h3 className="text-base font-bold text-white mb-1">{slide.title}</h3>
                    <p className="text-white/90 text-xs leading-tight">{slide.description}</p>
                  </div>
                ))}
              </div>

              <div className="flex justify-center gap-2 pb-3">
                {featureSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleSlideChange(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      currentSlide === index
                        ? 'bg-white w-6'
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-xl mb-4 text-center">
              {error}
            </div>
          )}

          {/* Plans */}
          <div className="space-y-2 mb-4">
            {/* Yearly plan */}
            <button
              onClick={() => setSelectedPlan('yearly')}
              className={`w-full p-4 rounded-2xl border-2 transition-all relative ${
                selectedPlan === 'yearly'
                  ? 'bg-white border-purple-500 shadow-lg'
                  : 'bg-white/90 border-transparent hover:border-white'
              }`}
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Star size={12} fill="currentColor" />
                  {t("subscription.popular", "Popular")}
                </span>
              </div>

              <div className="flex items-center justify-between pr-10">
                <div className="text-left">
                  <p className="font-bold text-gray-800">{t("subscription.annual", "Annual")}</p>
                  <p className="text-sm text-gray-500">{webYearlyMonthly}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-purple-600">{webYearlyPrice}/{t("subscription.an", "an")}</p>
                  <p className="text-xs text-green-600 font-medium">{t("subscription.economisez_50", "Économisez 50%")}</p>
                </div>
              </div>

              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'yearly'
                  ? 'border-purple-500 bg-purple-500'
                  : 'border-gray-300'
              }`}>
                {selectedPlan === 'yearly' && <Check size={14} className="text-white" />}
              </div>
            </button>

            {/* Monthly plan */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full p-4 rounded-2xl border-2 transition-all relative ${
                selectedPlan === 'monthly'
                  ? 'bg-white border-purple-500 shadow-lg'
                  : 'bg-white/90 border-transparent hover:border-white'
              }`}
            >
              <div className="flex items-center justify-between pr-10">
                <div className="text-left">
                  <p className="font-bold text-gray-800">{t("subscription.monthly", "Monthly")}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-purple-600">{webMonthlyPrice}/{t("subscription.mois", "mois")}</p>
                </div>
              </div>

              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'monthly'
                  ? 'border-purple-500 bg-purple-500'
                  : 'border-gray-300'
              }`}>
                {selectedPlan === 'monthly' && <Check size={14} className="text-white" />}
              </div>
            </button>
          </div>

          {/* Subscribe button */}
          <button
            onClick={handleStripeCheckout}
            disabled={stripeLoading}
            className="w-full bg-white text-purple-600 py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2"
          >
            {stripeLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-600 border-t-transparent"></div>
                {t("subscription.traitement_en_cours", "Traitement en cours...")}
              </>
            ) : (
              <>
                <Zap size={20} />
                {t("modals.subscribe_now", "Subscribe now")}
              </>
            )}
          </button>

          {/* Terms */}
          <p className="text-white/70 text-xs text-center mt-3 px-2">
            {t("subscription.renouvellement_auto_annulation_possible_a_tout_mom", "Renouvellement auto. Annulation possible à tout moment.")}
          </p>
        </div>
      </div>
    );
  }

  // Loading state (only on native, not web)
  if (!isWeb && (!isInitialized || revenueCatLoading)) {
    return (
      <div className={`h-screen-adaptive bg-gradient-to-b from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white font-medium">{t("subscription.chargement_des_offres", "Chargement des offres...")}</p>
        </div>
      </div>
    );
  }

  // Écran principal de sélection d'abonnement
  return (
    <div className={`h-screen-adaptive bg-gradient-to-b from-purple-600 via-pink-500 to-orange-400 ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
      {/* Header */}
      <div className="px-4 py-4">
        <button
          onClick={onBack}
          className="p-2 -ml-2 rounded-full hover:bg-white/20 transition-colors"
        >
          <ChevronLeft size={28} className="text-white" />
        </button>
      </div>

      <div className="px-4 pb-4">
        {/* Hero section */}
        <div className="text-center mb-4">
          <img
            src="/logo-white.png"
            alt="LilHero"
            className="h-16 w-auto mx-auto mb-2 drop-shadow-lg"
          />
          <h1 className="text-xl font-bold text-white mb-1 font-title">
            {t("subscription.ici_votre_enfant_est_le_heros", "Ici, votre enfant est le héros")}
          </h1>
        </div>

        {/* Features Carousel */}
        <div className="mb-4">
          <div
            ref={carouselRef}
            className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {featureSlides.map((slide, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 p-4 text-center"
                >
                  {slide.image ? (
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-24 object-cover rounded-xl mb-3"
                    />
                  ) : (
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
                      {slide.icon}
                    </div>
                  )}
                  <h3 className="text-base font-bold text-white mb-1">{slide.title}</h3>
                  <p className="text-white/90 text-xs leading-tight">{slide.description}</p>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-2 pb-3">
              {featureSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleSlideChange(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === index
                      ? 'bg-white w-6'
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Error message */}
        {(error || revenueCatError) && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-xl mb-4 text-center">
            {error || revenueCatError}
          </div>
        )}

        {/* Plans */}
        <div className="space-y-2 mb-4">
          {/* Yearly plan */}
          {annualPackage && (
            <button
              onClick={() => setSelectedPlan('yearly')}
              className={`w-full p-4 rounded-2xl border-2 transition-all relative ${
                selectedPlan === 'yearly'
                  ? 'bg-white border-purple-500 shadow-lg'
                  : 'bg-white/90 border-transparent hover:border-white'
              }`}
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Star size={12} fill="currentColor" />
                  {t("subscription.popular", "Popular")}
                </span>
              </div>

              <div className="flex items-center justify-between pr-10">
                <div className="text-left">
                  <p className="font-bold text-gray-800">{t("subscription.annual", "Annual")}</p>
                  <p className="text-sm text-gray-500">{yearlyMonthlyEquivalent}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-purple-600">{yearlyPrice}/{t("subscription.an", "an")}</p>
                  <p className="text-xs text-green-600 font-medium">{t("subscription.economisez_50", "Économisez 50%")}</p>
                </div>
              </div>

              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'yearly'
                  ? 'border-purple-500 bg-purple-500'
                  : 'border-gray-300'
              }`}>
                {selectedPlan === 'yearly' && (
                  <Check size={14} className="text-white" />
                )}
              </div>
            </button>
          )}

          {/* Monthly plan */}
          {monthlyPackage && (
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full p-4 rounded-2xl border-2 transition-all relative ${
                selectedPlan === 'monthly'
                  ? 'bg-white border-purple-500 shadow-lg'
                  : 'bg-white/90 border-transparent hover:border-white'
              }`}
            >
              <div className="flex items-center justify-between pr-10">
                <div className="text-left">
                  <p className="font-bold text-gray-800">{t("subscription.monthly", "Monthly")}</p>
                  <p className="text-sm text-gray-500">{monthlyPrice}/{t("subscription.mois", "mois")}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-purple-600">{monthlyPrice}/{t("subscription.mois", "mois")}</p>
                </div>
              </div>

              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'monthly'
                  ? 'border-purple-500 bg-purple-500'
                  : 'border-gray-300'
              }`}>
                {selectedPlan === 'monthly' && (
                  <Check size={14} className="text-white" />
                )}
              </div>
            </button>
          )}

          {/* No packages available */}
          {!monthlyPackage && !annualPackage && (
            <div className="bg-white/90 rounded-2xl p-4 text-center">
              <p className="text-gray-600">{t("subscription.aucune_offre_disponible_pour_le_moment", "Aucune offre disponible pour le moment.")}</p>
            </div>
          )}
        </div>

        {/* Subscribe button */}
        <button
          onClick={handleSubscribe}
          disabled={purchaseLoading || (!monthlyPackage && !annualPackage)}
          className="w-full bg-white text-purple-600 py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2"
        >
          {purchaseLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-600 border-t-transparent"></div>
              {t("subscription.traitement_en_cours", "Traitement en cours...")}
            </>
          ) : (
            <>
              <Zap size={20} />
              {t("modals.subscribe_now", "Subscribe now")}
            </>
          )}
        </button>

        {/* Terms */}
        <p className="text-white/70 text-xs text-center mt-2 px-2">
          {t("subscription.renouvellement_auto_annulation_possible_a_tout_mom", "Renouvellement auto. Annulation possible à tout moment.")}
        </p>

        {/* Restore purchases */}
        <button
          onClick={handleRestore}
          disabled={restoreLoading}
          className="w-full text-white/80 text-xs mt-2 py-1 hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {restoreLoading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
              {t("subscription.restauration", "Restauration...")}
            </>
          ) : (
            t("subscription.restore_my_purchases", "Restore my purchases")
          )}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionScreen;
